<script setup lang="ts">
import { round } from "lodash-es";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { boardActions } from "@/action/boardActions";
import StatusClassDistributionBar from "@/components/StatusDistribution/StatusClassDistributionBar.vue";
import ActionSource from "@/components/action/ActionSource.vue";
import BoardNumberInput from "@/components/input/BoardNumberInput.vue";
import CardsSkipButton from "@/components/ui/a11y/CardsSkipButton.vue";
import { loadState } from "@/components/utils/capacity";
import type { Iteration } from "@/model/session";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useIterationStore } from "@/store/iteration";
import { useSessionStore } from "@/store/session";
import { useWorkModeStore } from "@/store/workMode";
import { plusDays } from "@/utils/date";
import { toShortDateString } from "@/utils/dateFormat";
import { blurHandlerElem } from "@/utils/dom/dom";

import IterationFlag from "./IterationFlag.vue";
import TeamIterationMenu from "./TeamIterationMenu.vue";

const props = defineProps<{ index: number; readonly?: boolean }>();

const { t } = useI18n();

const flagType = computed(() => loadState(teamIteration.value.load, teamIteration.value.velocity));
const isExecutionMode = computed(() => useWorkModeStore().isExecutionMode);
const iteration = computed(() => useSessionStore().iterations[props.index]);
const distribution = computed(() => teamIteration.value.statusDistribution ?? new Map());

const teamBoard = computed(() => useBoardsStore().boardByType("team"));
const filters = computed(() => ({
  boardId: teamBoard.value.id,
  teamId: teamBoard.value.team.id,
  iterationId: iteration.value.id,
}));
const teamIteration = computed(() =>
  useIterationStore().iteration(useBoardsStore().boardByType("team"), props.index),
);
const iterationNow = computed(() =>
  useSessionStore().iterationProgress(new Date(), iteration.value),
);
const isCurrentIteration = computed(
  () => iterationNow.value.iterationsPassed > 0 && iterationNow.value.iterationsPassed < 1,
);

function changeVelocity(v: number) {
  boardActions.setVelocity("board", useBoardStore().currentBoard().id, props.index, v);
}

function dates(iter: Iteration) {
  return t("date.range", {
    from: toShortDateString(iter.start),
    to: toShortDateString(plusDays(iter.end, -1)),
  });
}
</script>

<!-- eslint-disable vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
<template>
  <section
    v-board-region
    class="team-iteration back-color"
    :class="flagType"
    :aria-label="$t('general.namedIteration', { name: iteration.name })"
    @click="blurHandlerElem"
  >
    <!-- eslint-enable vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
    <div class="head">
      <header class="row back-color">
        <h2 :title="iteration.name" class="iteration-name">
          {{ iteration.name }}
        </h2>
        <span class="iteration-date">
          {{ dates(iteration) }}
        </span>
      </header>
      <CardsSkipButton />
      <span class="row back-color">
        <div class="iteration-info">
          <IterationFlag :type="flagType" class="flag-icon" />
          <div class="metric-wrap">
            <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
            <label :for="`capacity-${iteration.id}`" class="metric-title">
              {{ $t("general.capacity") }}
            </label>
            <span class="metric-input-wrap">
              <BoardNumberInput
                :id="`capacity-${iteration.id}`"
                :readonly
                class="metric-input"
                :value="teamIteration.velocity"
                :maxlength="3"
                @input-on-blur="changeVelocity"
                @dblclick.stop
              />
            </span>
          </div>
          <div class="metric-wrap">
            <span class="metric-title" aria-hidden="true">
              {{ $t("loadCapacityChart.load") }}
            </span>
            <div class="screen-reader-only">
              {{ $t("label.iteration.load", { value: round(teamIteration.load, 1) }) }}
            </div>
            <span class="metric-input-wrap">
              <BoardNumberInput
                class="metric-input"
                readonly
                :maxlength="4"
                :value="round(teamIteration.load, 1)"
                tabindex="-1"
                aria-hidden="true"
              />
            </span>
          </div>
        </div>
        <ActionSource value="board-button">
          <TeamIterationMenu :iteration />
        </ActionSource>
      </span>
    </div>
    <div
      v-if="isExecutionMode && isCurrentIteration"
      class="time-line"
      :style="{ width: iterationNow.iterationsPassed * 100 + '%' }"
    >
      <svg
        :aria-label="
          $t('label.iteration.progress', { value: round(iterationNow.iterationsPassed * 100, 1) })
        "
      >
        <line x1="0" y1="0" x2="0" y2="100%" />
      </svg>
    </div>
    <StatusClassDistributionBar
      v-if="isExecutionMode"
      class="iteration-distribution"
      :value="distribution"
      :iteration
      :filters
      source-item-type="iteration"
      :title="iteration.name"
    />
  </section>
</template>

<style lang="scss">
@use "@/styles/font";
@use "@/styles/board";
@use "@/styles/variables/colors";
@use "sass:color";
@use "@/styles/time-line" as *;
@use "@/styles/mixins/a11y";
@use "@/styles/mixins/shadow";
@use "@/styles/mixins/typography";
@use "@/styles/z-index";

.team-iteration {
  @include a11y.board-section;

  &.default,
  &.default .back-color {
    background-color: colors.$background-ui;
  }

  &.ok,
  &.ok .back-color {
    background-color: colors.$alternative-blue-background-light;
  }

  &.warn,
  &.warn .back-color {
    background-color: colors.$alternative-brown-background-light;
  }

  &.error,
  &.error .back-color {
    background-color: colors.$alternative-red-background-light;
  }

  .time-line {
    top: 0;
    left: 0;
  }

  .head {
    display: flex;
    flex-direction: column;
    align-items: baseline;
    justify-content: space-between;
    gap: board.len(3px);

    .row {
      display: flex;
      justify-content: space-between;
      width: 100%;

      .iteration-name {
        @include board.board-control($background: inherit);
        @include typography.medium;

        font-size: board.len(14px);
        overflow-wrap: break-word;
      }

      .iteration-date {
        @include board.board-control($background: inherit);
        @include typography.small;

        font-size: board.len(13px);
        color: colors.$text-subtle;
        white-space: nowrap;
      }

      .iteration-info {
        display: flex;
        align-items: center;
        gap: board.len(6px);
        background-color: inherit;
      }

      .flag-icon {
        @include board.board-control($background: inherit);

        width: board.len(20px);
        height: board.len(20px);
      }

      .metric-wrap {
        @include board.board-control($background: inherit);

        display: flex;
        align-items: center;
        gap: board.len(6px);

        .metric-title {
          color: colors.$text-subtle;
          font-size: board.len(11px);
          font-weight: font.$weight-semi-bold;
        }

        .metric-input-wrap {
          display: inline-flex;
          width: board.len(28px);
          height: board.len(20px);

          .metric-input {
            text-align: center;
            font-size: board.len(10px);
          }
        }

        .screen-reader-only {
          @include a11y.screen-reader-only;

          margin-right: board.len(-6px);
        }
      }
    }
  }

  .iteration-distribution {
    @include board.board-control($background: inherit);

    height: board.len(10px);
    position: absolute;
    top: board.len(3px);
    left: board.len(12px);
    right: board.len(12px);
  }
}

// This is inside a tooltip attached to the body
// so the styles have to be outside the component's scope
.load-stat {
  @include shadow.default;

  padding: 4px;
  background-color: colors.$palette-white-alpha-90;
  border-radius: 50%;
}
</style>

<style lang="scss">
.tippy-box[data-theme~="no-background"] {
  background-color: transparent;
  max-width: unset !important;

  .tippy-arrow {
    display: none;
  }
}
</style>
